/**
 * Utility functions for analyzing A/B test results
 */

interface VariantData {
  id: string;
  name: string;
  visitors: number;
  conversions: number;
  conversionRate: number;
  engagementRate: number;
  avgTimeOnPage: string;
  bounceRate: number;
}

interface TestResult {
  hasSignificance: boolean;
  confidenceLevel: number;
  winner: string | null;
  improvementPercentage: number | null;
  message: string;
}

/**
 * Calculate statistical significance between variants
 * Uses a simplified version of the chi-squared test
 */
export const calculateSignificance = (
  variantA: VariantData,
  variantB: VariantData,
  confidenceThreshold = 0.95
): TestResult => {
  // Calculate conversion rates
  const rateA = variantA.conversionRate / 100;
  const rateB = variantB.conversionRate / 100;
  
  // Calculate standard errors
  const seA = Math.sqrt((rateA * (1 - rateA)) / variantA.visitors);
  const seB = Math.sqrt((rateB * (1 - rateB)) / variantB.visitors);
  
  // Calculate z-score
  const zScore = Math.abs(rateA - rateB) / Math.sqrt(seA * seA + seB * seB);
  
  // Convert z-score to confidence level (simplified)
  const confidenceLevel = Math.min(0.99, (zScore / 3) * 0.99);
  
  // Determine if the result is statistically significant
  const hasSignificance = confidenceLevel >= confidenceThreshold;
  
  // Calculate improvement percentage
  const improvementPercentage = rateB > rateA
    ? ((rateB - rateA) / rateA) * 100
    : ((rateA - rateB) / rateB) * 100;
  
  // Determine the winner
  const winner = rateA > rateB ? variantA.id : rateB > rateA ? variantB.id : null;
  
  // Create a message
  let message = '';
  if (hasSignificance) {
    message = `${winner === variantA.id ? variantA.name : variantB.name} is the winner with ${confidenceLevel.toFixed(2) * 100}% confidence. It shows a ${improvementPercentage.toFixed(1)}% improvement.`;
  } else {
    message = `The test is inconclusive. We need more data to determine a winner with ${confidenceThreshold * 100}% confidence.`;
  }
  
  return {
    hasSignificance,
    confidenceLevel,
    winner,
    improvementPercentage,
    message,
  };
};

/**
 * Find the best performing variant from a list of variants
 */
export const findBestVariant = (variants: VariantData[]): VariantData => {
  return [...variants].sort((a, b) => b.conversionRate - a.conversionRate)[0];
};

/**
 * Calculate required sample size for a given minimum detectable effect
 * @param baselineConversionRate The current conversion rate (e.g., 0.05 for 5%)
 * @param minimumDetectableEffect The smallest improvement you want to detect (e.g., 0.2 for 20%)
 * @param confidenceLevel Desired confidence level (default: 0.95 for 95%)
 * @param power Statistical power (default: 0.8 for 80%)
 * @returns The required number of visitors per variant
 */
export const calculateRequiredSampleSize = (
  baselineConversionRate: number,
  minimumDetectableEffect: number,
  confidenceLevel = 0.95,
  power = 0.8
): number => {
  // Convert percentage to decimal if needed
  const rate = baselineConversionRate > 1 ? baselineConversionRate / 100 : baselineConversionRate;
  
  // Z-scores for confidence level and power
  const zAlpha = confidenceLevel === 0.95 ? 1.96 : confidenceLevel === 0.99 ? 2.58 : 1.65;
  const zBeta = power === 0.8 ? 0.84 : power === 0.9 ? 1.28 : 0.67;
  
  // Calculate expected conversion rate after improvement
  const expectedRate = rate * (1 + minimumDetectableEffect);
  
  // Calculate sample size using simplified formula
  const p1 = rate;
  const p2 = expectedRate;
  const pAvg = (p1 + p2) / 2;
  
  const sampleSize = Math.ceil(
    ((zAlpha + zBeta) ** 2 * pAvg * (1 - pAvg) * 2) / (p2 - p1) ** 2
  );
  
  return sampleSize;
};

/**
 * Check if we have enough data to make a decision
 */
export const hasEnoughData = (
  variants: VariantData[],
  minimumVisitorsPerVariant = 1000
): boolean => {
  return variants.every(variant => variant.visitors >= minimumVisitorsPerVariant);
};

/**
 * Generate recommendations based on test results
 */
export const generateRecommendations = (variants: VariantData[]): string[] => {
  const recommendations: string[] = [];
  const bestVariant = findBestVariant(variants);
  const hasEnough = hasEnoughData(variants);
  
  if (hasEnough) {
    recommendations.push(
      `The "${bestVariant.name}" variant is the winner with a ${bestVariant.conversionRate}% conversion rate. We recommend making this your default landing page.`
    );
  } else {
    recommendations.push(
      `Continue testing until you reach at least 1,000 visitors per variant for more conclusive results.`
    );
  }
  
  // Check if there's a clear pattern in the data
  const sortedVariants = [...variants].sort((a, b) => b.conversionRate - a.conversionRate);
  if (sortedVariants[0].conversionRate > sortedVariants[1].conversionRate * 1.2) {
    recommendations.push(
      `The "${sortedVariants[0].name}" variant is significantly outperforming others. Consider creating a new variant that builds on its strengths.`
    );
  }
  
  return recommendations;
}; 